import { Component, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center p-8 bg-black/20 border border-white/5 rounded-xl min-h-[200px]">
          <AlertTriangle className="size-8 text-amber-500 mb-3" />
          <p className="text-[11px] font-black uppercase tracking-widest text-white/60 mb-2">Component Error</p>
          <p className="text-[10px] text-white/30 text-center max-w-[300px] mb-4">
            {this.state.error?.message || "An unexpected error occurred while loading this component."}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 rounded-lg transition-all text-[10px] font-black uppercase tracking-wider"
          >
            <RefreshCw className="size-3" />
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
